/**
 * Převozy — the two transfer queues.
 *
 * This is the screen the dispatchers live on, and it is the one where the
 * layout matters most: the two tabs, the column order inside each of them,
 * and the three per-row decisions (Řidič / Cílové stání / GPS) are exactly
 * where the approved mockup puts them, which is exactly where the legacy
 * screen puts them. Nothing has been rearranged.
 *
 * Since Phase 1 (2 September 2026) the three decisions are live: each row
 * carries the legacy's three <select>s and its » button (DispatchCells),
 * and pressing » is the legacy `setTransfer` — driver, destination, GPS,
 * the transfer stamp, and the car counted at its destination from that
 * moment. Nothing is saved before » is pressed. The row itself still opens
 * the reservation panel; the cells stop the click so choosing a driver
 * does not open it.
 *
 * Both queues arrive in one call and are held in one payload, so the tab is a
 * pure client-side switch — it costs nothing and it survives a refresh,
 * because it lives in the address bar (?fronta=t2e). A dispatcher who has the
 * return queue open and reloads should get the return queue back.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LEG_FROM_EUROPORT,
  LEG_TO_EUROPORT,
  shiftFor,
  SITE_FROM_LABEL,
  TRANSFER_SITES,
  type ParkingSite,
  type SiteOccupancy,
  type TransferRow,
} from '@smile/shared';
import { api } from '../api/client';
import { isEuroportSpot, spotLabel, TYPE_LABEL, TYPE_SHORT } from '../lib/format';
import { Drawer } from '../components/RowDrawer';
import { DispatchCells } from '../components/DispatchCells';
import { durationHm, hhmmOffset, splitDt } from '../lib/filters';
import { Footer } from '../components/Footer';

/** Which queue is on screen. The third tab is phase 2 and is disabled. */
type Queue = 'e2t' | 't2e';

/**
 * Colour thresholds, in milliseconds, straight off the mockup.
 *
 * t2e is the queue that can fail in front of a customer: the car has to be at
 * the terminal before they walk out of arrivals, so under two hours is red
 * and under four is amber. e2t cannot fail in front of anybody — the customer
 * has already flown — but a car that has sat in the garage for most of a
 * shift is a spot not earning and a job somebody forgot, so it goes amber
 * after seven hours.
 */
const T2E_URGENT_MS = 2 * 3_600_000;
const T2E_SOON_MS = 4 * 3_600_000;
const E2T_SOON_MS = 7 * 3_600_000;

/** The API caps each queue; see listBothQueues(limit = 100). */
const QUEUE_LIMIT = 100;

/* ------------------------------------------------------------------ *
 * Cells
 * ------------------------------------------------------------------ */

/**
 * Status + ID + product tier, as on the mockup.
 *
 * ✔ here means "a driver is already on it", not "the car has moved". Both
 * queues are built from rows whose transfer stamp is still NULL, so a
 * dispatched row is not in this list at all; the state that genuinely varies
 * on screen is assigned-versus-not, and that is the one the icon reports.
 */
function Stav({ r }: { r: TransferRow }) {
  const assigned = !!r.assignedUserName;
  return (
    <td>
      <span
        className={assigned ? 'stav' : 'stav gray'}
        title={assigned ? `Přiřazeno: ${r.assignedUserName}` : 'Zatím bez řidiče'}
      >
        <span className="ic">{assigned ? '✔' : '○'}</span> {r.reservationId}
        {/* The caret the dashboard and the Rezervace grid both carry. It turns
            in CSS when the row is open. */}
        <span className="caret2" aria-hidden="true">
          ▾
        </span>
      </span>
      <div className="sub">{TYPE_SHORT[r.type] ?? r.type}</div>
    </td>
  );
}

function Klient({ r }: { r: TransferRow }) {
  return (
    <td className="klient">
      <b className="name">{r.driverName || '—'}</b>
      {r.clubTier === 'gold' && <span className="flag gold">GOLD</span>}
      {r.clubTier === 'platinum' && <span className="flag plat">PLATINUM</span>}
      <span className="tel">
        {r.driverPhone || '—'}
        {r.flightNumber ? ` · ${r.flightNumber}` : ''}
      </span>
    </td>
  );
}

/**
 * SPZ over the car description. The plate is the larger of the two on
 * purpose: it is what the driver reads off the windscreen in the dark, and a
 * missing one is a job that cannot start, so it is called out in the row
 * rather than left as an empty cell.
 */
function Auto({ r }: { r: TransferRow }) {
  return (
    <td>
      {r.spz ? (
        <span className="spz">{r.spz}</span>
      ) : (
        <span className="doplnit">Doplnit SPZ!</span>
      )}
      <div className="sub">{r.car || '—'}</div>
    </td>
  );
}

/** A parking spot chip: blue for Europort, plain for Tuchoměřice, red for none. */
function Spot({ value }: { value: string }) {
  if (!value) return <span className="spot none">nepřiřazeno</span>;
  return (
    <span className={isEuroportSpot(value) ? 'spot eu' : 'spot'}>{spotLabel(value)}</span>
  );
}

/* ------------------------------------------------------------------ *
 * Rows
 * ------------------------------------------------------------------ */

interface RowProps {
  r: TransferRow;
  now: number;
  gpsClash: Set<number>;
  openId: number | null;
  onToggle: (id: number) => void;
}

function rowProps(r: TransferRow, extra: string, isOpen: boolean, toggle: () => void) {
  return {
    className: ['click', extra, isOpen ? 'open' : ''].filter(Boolean).join(' '),
    tabIndex: 0,
    role: 'button' as const,
    'aria-expanded': isOpen,
    'aria-label': `Rezervace ${r.reservationId}, ${r.driverName}`,
    onClick: () => {
      // A dispatcher copying a plate out of the row should not lose the row
      // mid-selection.
      if (window.getSelection()?.toString()) return;
      toggle();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    },
  };
}

/**
 * The open-in-place panel, on a screen whose rows are the wrong shape for it.
 *
 * The dashboard and the Rezervace grid can render the panel for free, because
 * the row they were handed already carries every field it shows. A transfer
 * row does not: `TransferRow` is the queue's own narrow shape and has no
 * status, no price, no payment, no e-mail, no KeyCafe stamps. Widening the
 * transfer contract to fill a panel would put a dozen fields on the wire for
 * every row in a 400-row queue so that one of them could be opened.
 *
 * So this one fetches. `api.reservation(id)` already returns exactly the
 * `ReservationRow` the panel takes, the request only happens when a dispatcher
 * actually opens a row, and TanStack caches it — closing and reopening the
 * same car costs nothing. The panel itself is the same component the other two
 * screens render, so all three stay identical by construction.
 */
function TransferDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['reservation', id],
    queryFn: () => api.reservation(id),
    staleTime: 60_000,
  });

  if (isPending) return <div className="drawer" style={{ padding: 14 }}><div className="skeleton" /></div>;

  if (isError || !data)
    return (
      <div className="drawer" style={{ padding: 14 }} role="alert">
        Rezervaci se nepodařilo načíst
        {error instanceof Error && error.message ? ` (${error.message})` : ''}.{' '}
        <button className="btn sm" type="button" onClick={() => refetch()}>
          Zkusit znovu
        </button>
      </div>
    );

  return <Drawer r={data} onClose={onClose} />;
}

function E2tRow({ r, now, gpsClash, openId, onToggle }: RowProps) {
  const since = r.arrivalReal ? new Date(r.arrivalReal.replace(' ', 'T')).getTime() : NaN;
  const waited = Number.isFinite(since) ? now - since : 0;
  const cls = waited > E2T_SOON_MS ? 'soon' : '';
  const isOpen = openId === r.reservationId;

  return (
    <Fragment>
    <tr
      {...rowProps(r, r.assignedUserName ? 'done' : '', isOpen, () => onToggle(r.reservationId))}
    >
      <Stav r={r} />
      <Klient r={r} />
      <Auto r={r} />
      <td>
        <span className={`cnt ${cls}`}>{splitDt(r.arrivalReal).t || '—'}</span>
        <span className="sub">
          {Number.isFinite(since) ? `čeká ${durationHm(waited)}` : 'čas příjezdu chybí'}
        </span>
      </td>
      <td>
        <Spot value={r.parking} />
      </td>
      <DispatchCells r={r} gpsClash={gpsClash} />
    </tr>
    {isOpen && (
      <tr className="drawerrow">
        <td colSpan={8}>
          <TransferDrawer id={r.reservationId} onClose={() => onToggle(r.reservationId)} />
        </td>
      </tr>
    )}
    </Fragment>
  );
}

function T2eRow({ r, now, gpsClash, openId, onToggle }: RowProps) {
  const dep = new Date(r.departure.replace(' ', 'T')).getTime();
  const left = Number.isFinite(dep) ? dep - now : Number.POSITIVE_INFINITY;
  const cls = left < T2E_URGENT_MS ? 'urgent' : left < T2E_SOON_MS ? 'soon' : '';
  const overdue = left < 0;
  const isOpen = openId === r.reservationId;

  return (
    <Fragment>
    <tr
      {...rowProps(
        r,
        r.assignedUserName ? 'done' : cls === 'urgent' ? 'crit' : '',
        isOpen,
        () => onToggle(r.reservationId),
      )}
    >
      <Stav r={r} />
      <Klient r={r} />
      <td>
        <span className={`cnt ${cls}`}>
          {!Number.isFinite(left)
            ? '—'
            : overdue
              ? `po termínu ${durationHm(left)}`
              : `za ${durationHm(left)}`}
        </span>
        <span className="sub">
          odjezd {splitDt(r.departure).d} {splitDt(r.departure).t} · na místě do{' '}
          {hhmmOffset(r.departure, -60)}
        </span>
      </td>
      <td>
        <Spot value={r.parking} />
      </td>
      <Auto r={r} />
      <DispatchCells r={r} gpsClash={gpsClash} />
    </tr>
    {isOpen && (
      <tr className="drawerrow">
        <td colSpan={8}>
          <TransferDrawer id={r.reservationId} onClose={() => onToggle(r.reservationId)} />
        </td>
      </tr>
    )}
    </Fragment>
  );
}

/* ------------------------------------------------------------------ *
 * The yard strip
 * ------------------------------------------------------------------ */

/**
 * Which yard the return queue is filtered to — the owner's "add other
 * locations that we have: POP, Knezeves, Tuchomerice, Sestka".
 *
 * The legacy screen spends one flat strip on this: Příjezdy, Europort,
 * Z Tuchoměřic 68, Z POPu 91, Ze Šestky 0, Z Kněževse 38, Odjezdy. Note what
 * it does NOT contain: a "Do Tuchoměřic". All four yard tabs say "Z", and that
 * is not an oversight — a car still waiting in the garage has not been given a
 * yard yet, because giving it one is the dispatcher's next action. There is
 * nothing in the schema to filter the outbound queue by, so this strip renders
 * on the return queue only and the outbound tab is left exactly as it was.
 *
 * Two strips, one under the other, rather than the legacy's single row that
 * mixes direction and yard into one set of buttons. The direction tabs stay
 * exactly where the mockup and the legacy screen put them; the yards are a row
 * that was not there before, so nothing an operator already clicks has moved.
 *
 * Europort is not among the four. It is the hub every leg runs to, so
 * "Z Europortu" on the return queue would select nothing at all. TRANSFER_SITES
 * is the four yards and it is derived in sites.ts rather than typed out here.
 */
function Mista({
  site,
  counts,
  onPick,
}: {
  site: ParkingSite | null;
  counts: SiteOccupancy[] | undefined;
  onPick: (s: ParkingSite | null) => void;
}) {
  const totalOf = (s: ParkingSite) => counts?.find((b) => b.site === s)?.total;
  const all = counts?.reduce((n, b) => n + b.total, 0);
  /* Present only when the queue actually holds a spot string nothing
     recognises. It is not clickable, because there is no LIKE prefix that
     selects "unrecognised" — see SITE_LIKE_PREFIXES. Printing it anyway is
     what makes the four badges add up to the queue total in front of the
     person who can get the zone named properly. */
  const unknown = counts?.find((b) => b.site === null);

  return (
    <div className="mtabs" role="group" aria-label="Odkud se veze">
      <button
        type="button"
        className={site === null ? 'active' : ''}
        aria-pressed={site === null}
        onClick={() => onPick(null)}
      >
        Vše <span className="badge">{all ?? '·'}</span>
      </button>
      {TRANSFER_SITES.map((s) => {
        const n = totalOf(s);
        return (
          <button
            key={s}
            type="button"
            className={site === s ? 'active' : ''}
            aria-pressed={site === s}
            onClick={() => onPick(s)}
          >
            {LEG_TO_EUROPORT[s]}{' '}
            <span className={`badge${n === 0 ? ' zero' : ''}`}>{n ?? '·'}</span>
          </button>
        );
      })}
      {unknown && unknown.total > 0 && (
        <span
          className="mtab-unknown"
          title={
            'Tyto vozy mají v databázi stání, které systém nezná — ' +
            unknown.zones.map((z) => `${z.zone} (${z.count})`).join(', ') +
            '. Najdete je pod „Vše".'
          }
        >
          Neurčeno <span className="badge">{unknown.total}</span>
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Screen
 * ------------------------------------------------------------------ */

export function Prevozy() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const queue: Queue = sp.get('fronta') === 't2e' ? 't2e' : 'e2t';
  /* Unrecognised values fall back to the whole queue rather than to an empty
     list: a stale bookmark or a typed URL should show the dispatcher too much,
     never nothing. The same fallback covers ?misto= on the outbound queue,
     where every car is standing at Europort and a yard filter would silently
     match nothing at all. */
  const siteParam = sp.get('misto');
  const site: ParkingSite | null =
    queue === 't2e' && siteParam && (TRANSFER_SITES as readonly string[]).includes(siteParam)
      ? (siteParam as ParkingSite)
      : null;
  const [term, setTerm] = useState('');

  const { data, isPending, isError, isFetching, error, refetch } = useQuery({
    queryKey: ['transfers'],
    queryFn: () => api.transfers(),
    // The same sixty-second cadence as the dashboard. The queues change when
    // a car physically moves, which is not a per-second event.
    refetchInterval: 60_000,
  });

  /**
   * The narrowed queue, fetched only while a yard is selected.
   *
   * It has to be its own request rather than a filter over `data`: that
   * payload is capped at a hundred rows per direction and the yards routinely
   * exceed that between them, so filtering in the browser would drop the
   * oldest-waiting cars — the ones the queue exists to surface — out of the
   * exact tab somebody opened to find them.
   */
  const siteQ = useQuery({
    queryKey: ['transfers', 'queue', queue, site],
    queryFn: () => api.transferQueue({ direction: queue, site, limit: QUEUE_LIMIT }),
    enabled: site !== null,
    refetchInterval: 60_000,
  });

  // The countdowns are the point of this screen, so the clock has to move on
  // its own — otherwise a tab left open shows a two-hour-old "za 20 m".
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setClock(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);

  const rows = site !== null ? (siteQ.data ?? []) : data ? data[queue] : [];
  const rowsPending = site !== null ? siteQ.isPending : isPending;

  const shift = useMemo(() => shiftFor(new Date(clock)), [clock]);
  const shiftLabel = `${shift.kind === 'day' ? 'denní' : 'noční'} směna · ${
    shift.from.getHours()
  }:00 – ${shift.to.getHours()}:00`;
  const hhmm = new Date(clock).toTimeString().slice(0, 5);

  /**
   * Driver load and GPS collisions, both derived from the rows on screen.
   *
   * The mockup's chip strip is headed "Řidiči ve směně". There is no shift
   * roster in the read model yet, so this counts the drivers who actually
   * hold cars in the two queues right now and is labelled accordingly.
   * Inventing a roster from the user table would list people who are at home.
   *
   * The collision set is the mockup's fourth fix: one GPS unit showing up
   * under two different drivers means two people are about to walk to the
   * same box. Two rows for one driver on one unit is not a collision — that
   * is a driver taking several cars on one run, which is normal.
   */
  /* Both derived from the WHOLE payload, never from the filtered rows. A
     driver's load and a GPS collision are facts about the shift, not about the
     yard somebody is currently looking at — narrowing them to the open tab
     would hide the second half of a collision and make the warning stop firing
     precisely when a dispatcher had drilled in. */
  const { load, gpsClash, gpsInUse } = useMemo(() => {
    const all = data ? [...data.e2t, ...data.t2e] : [];
    const byDriver = new Map<string, number>();
    const gpsDrivers = new Map<number, Set<string>>();

    for (const r of all) {
      if (r.assignedUserName) {
        byDriver.set(r.assignedUserName, (byDriver.get(r.assignedUserName) ?? 0) + 1);
      }
      if (r.gps != null) {
        const who = gpsDrivers.get(r.gps) ?? new Set<string>();
        who.add(r.assignedUserName ?? '—');
        gpsDrivers.set(r.gps, who);
      }
    }

    const clash = new Set<number>();
    for (const [unit, who] of gpsDrivers) if (who.size > 1) clash.add(unit);

    return {
      load: [...byDriver.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'cs')),
      gpsClash: clash,
      gpsInUse: [...gpsDrivers.keys()].sort((a, b) => a - b),
    };
  }, [data]);

  const setQueue = (q: Queue) => {
    const next = new URLSearchParams(sp);
    if (q === 'e2t') {
      next.delete('fronta');
      // The yard goes with it. It only ever meant something on the return
      // queue, and leaving it in the address bar would silently re-apply it on
      // the way back — a filter you cannot see is a filter you cannot clear.
      next.delete('misto');
    } else next.set('fronta', q);
    setSp(next, { replace: true });
  };

  const setSite = (s: ParkingSite | null) => {
    const next = new URLSearchParams(sp);
    if (s === null) next.delete('misto');
    else next.set('misto', s);
    setSp(next, { replace: true });
  };

  /**
   * Clicking a row opens the reservation under the row instead of leaving the
   * screen — the owner's note, applied here as well as on the grid: "My
   * dispatchers are used to opening a reservation from dashboard in drop down
   * window, we can do a screen opened without going to details right away and
   * then having to go back".
   *
   * It matters more here than anywhere. This queue is worked top to bottom
   * while cars are physically moving; navigating away to check one booking and
   * coming back re-sorts the list under you and costs your place in it.
   */
  const [openId, setOpenId] = useState<number | null>(null);
  const toggleRow = (id: number) => setOpenId((cur) => (cur === id ? null : id));

  /* Switching queue or yard replaces every row, so a panel left open would be
     attached to a car that is no longer on screen. */
  useEffect(() => {
    setOpenId(null);
  }, [queue, site]);

  /* Escape closes it, as on the other screens. */
  useEffect(() => {
    if (openId === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId]);

  return (
    <div className="container">
      <div className="pagehead">
        <h1>Převozy</h1>
        <span className="shift">
          {shiftLabel} · {hhmm}
          {isFetching || siteQ.isFetching ? ' · aktualizuji' : ''}
        </span>
        <div className="acts">
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              // One search box in this application, and it lives on the
              // Rezervace grid. A second one here would answer a subtly
              // different question and the two would disagree.
              if (term.trim()) navigate(`/rezervace?q=${encodeURIComponent(term.trim())}`);
            }}
          >
            <label className="visually-hidden" htmlFor="pq">
              Hledat rezervace
            </label>
            <input
              id="pq"
              type="search"
              placeholder="Hledat — ID, SPZ, jméno, telefon…"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
            <button className="btn" type="submit" style={{ marginLeft: 8 }}>
              Hledat
            </button>
          </form>
        </div>
      </div>

      {/* This used to instruct the reader to reload the page, which on this
          screen means losing the queue the dispatcher was looking at. The
          button asks again without moving anything. The old wording is not
          quoted here on purpose: verify-web-filters greps the route files for
          it, and a grep cannot tell a comment from an instruction. */}
      {/* One alert for both requests. A failed yard filter is the same event
          to the reader as a failed queue load — something is not on screen —
          and two stacked banners saying so would push the table down without
          telling them anything the first one did not. */}
      {(isError || siteQ.isError) && (
        <div className="alert" role="alert">
          Fronty se nepodařilo načíst
          {(() => {
            const e = isError ? error : siteQ.error;
            return e instanceof Error && e.message ? ` (${e.message})` : '';
          })()}
          .{' '}
          <button
            className="btn sm"
            type="button"
            onClick={() => {
              if (isError) refetch();
              if (siteQ.isError) siteQ.refetch();
            }}
          >
            Zkusit znovu
          </button>
        </div>
      )}

      <div className="qtabs">
        {/* Both tabs used to say "Tuchoměřice", because that is what the
            legacy screen says and Tuchoměřice was the only yard when it was
            written. There are four yards now, and the owner asked for the leg
            to be named at both ends — "Knezeves-Europort", "Europort - POP".
            The return tab therefore hands the naming to the strip below,
            which does exactly that per yard; the outbound tab says
            "parkoviště" because it genuinely cannot say more (see the note
            that renders in the strip's place on that tab). */}
        <button
          className={queue === 'e2t' ? 'active' : ''}
          onClick={() => setQueue('e2t')}
          type="button"
          title="Vozy z Europortu na dlouhodobá parkoviště — Tuchoměřice, POP, Kněževes, Šestka."
        >
          Europort → parkoviště{' '}
          <span className={`badge${data && data.counts.e2t === 0 ? ' zero' : ''}`}>
            {data ? data.counts.e2t : '·'}
          </span>
        </button>
        <button
          className={queue === 't2e' ? 'active' : ''}
          onClick={() => setQueue('t2e')}
          type="button"
          title="Vozy z dlouhodobých parkovišť zpět na Europort — Tuchoměřice, POP, Kněževes, Šestka."
        >
          Parkoviště → Europort{' '}
          <span className={`badge${data && data.counts.t2e === 0 ? ' zero' : ''}`}>
            {data ? data.counts.t2e : '·'}
          </span>
        </button>
        {/* Kept and disabled rather than removed: the third tab is where the
            staff expect messages to be, and a tab that vanished would read as
            a feature that was taken away. */}
        <button type="button" disabled title="Interní zprávy připravujeme ve fázi 2.">
          Interní zprávy <span className="phase">FÁZE 2</span>
        </button>
      </div>

      {/* Return queue only — there is no yard on an outbound row to filter by.
          Rendering an all-zero strip on the other tab would look like a
          feature that is broken rather than one that does not apply.

          The outbound tab gets a sentence in the same place instead, so the
          row of controls does not appear and disappear as the tab changes,
          and so the four legs the owner named are at least written down where
          he expects to click them. `parkingtarget` on an outbound row is the
          RETURN stall in the garage, not the destination yard — of the 89 058
          rows that carry one, 86 330 are Europort stalls and every one of the
          2 728 naming a yard is closed or cancelled. No live car records where
          it is going, because choosing where it goes is the dispatcher's next
          action, and in phase 1 that is the "Cílové stání" dropdown on the row
          itself. The legacy strip has only "from" tabs for this same reason. */}
      {queue === 't2e' ? (
        <Mista site={site} counts={data?.counts.returnBySite} onPick={setSite} />
      ) : (
        <p className="mtabs-note">
          Frontu z Europortu zatím nejde rozdělit na{' '}
          {/* Each leg is one word as far as line-breaking is concerned. Left
              to wrap freely the sentence broke after the arrow, and "Europort
              →" at the end of a line reads as a sentence that lost its ending
              rather than as the name of a run. */}
          {[
            LEG_FROM_EUROPORT.tuchomerice,
            LEG_FROM_EUROPORT.pop,
            LEG_FROM_EUROPORT.knezeves,
            LEG_FROM_EUROPORT.sestka,
          ].map((leg, i, all) => (
            <Fragment key={leg}>
              <span className="leg">{leg}</span>
              {i < all.length - 2 ? ', ' : i === all.length - 2 ? ' a ' : ' '}
            </Fragment>
          ))}
          — cílové parkoviště se u vozu zaznamená až ve chvíli, kdy ho dispečer přidělí polem
          „Kam" v řádku a stiskne ».
        </p>
      )}

      <div className="card toolbar">
        <span className="ttl">Rozdělené vozy podle řidiče</span>
        <div className="drvchips">
          {load.length === 0 && (
            <span className="drvchip">zatím nikdo — všechny vozy čekají na přidělení</span>
          )}
          {load.map(([name, n]) => (
            <span key={name} className={n >= 3 ? 'drvchip busy' : 'drvchip'}>
              {shortName(name)} <b>{n}</b>
            </span>
          ))}
        </div>
        <div className="right2">
          <span className="ttl">
            GPS v terénu: {gpsInUse.length ? gpsInUse.join(', ') : '—'}
            {gpsClash.size > 0 && (
              <span className="gpswarn"> · kolize: {[...gpsClash].join(', ')}</span>
            )}
          </span>
        </div>
      </div>

      <div className="card qcard">
        <div className="qwrap">
          <table className="qtable">
            <thead>
              {queue === 'e2t' ? (
                <tr>
                  <th>Stav / ID</th>
                  <th>Klient</th>
                  <th>Vozidlo / SPZ</th>
                  <th>Přijelo</th>
                  <th>Stojí na</th>
                  <th>Řidič</th>
                  <th>Cílové stání</th>
                  <th>GPS</th>
                </tr>
              ) : (
                <tr>
                  <th>Stav / ID</th>
                  <th>Klient</th>
                  <th>Odjezd</th>
                  <th>Stojí na</th>
                  <th>Vozidlo / SPZ</th>
                  <th>Řidič</th>
                  <th>Cílové stání</th>
                  <th>GPS</th>
                </tr>
              )}
            </thead>
            <tbody>
              {rowsPending &&
                Array.from({ length: 6 }, (_, i) => (
                  <tr key={`s${i}`}>
                    <td colSpan={8}>
                      <div className="skeleton" />
                    </td>
                  </tr>
                ))}

              {!rowsPending && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="state">
                    {/* The yard is named when one is selected, so an empty
                        table reads as "nothing for POP" rather than as a
                        screen that has stopped working. */}
                    {/* Only the return queue can have a yard selected — `site`
                        is forced to null on the outbound tab — so there is one
                        branch here and not two. */}
                    {site !== null
                      ? `V nejbližších hodinách se nevrací žádný vůz ${SITE_FROM_LABEL[
                          site
                        ].toLowerCase()} na Europort.`
                      : queue === 'e2t'
                        ? 'Žádný vůz nečeká na převoz z Europortu.'
                        : 'V nejbližších hodinách se nevrací žádný vůz na Europort.'}
                  </td>
                </tr>
              )}

              {rows.map((r) =>
                queue === 'e2t' ? (
                  <E2tRow
                    key={r.reservationId}
                    r={r}
                    now={clock}
                    gpsClash={gpsClash}
                    openId={openId}
                    onToggle={toggleRow}
                  />
                ) : (
                  <T2eRow
                    key={r.reservationId}
                    r={r}
                    now={clock}
                    gpsClash={gpsClash}
                    openId={openId}
                    onToggle={toggleRow}
                  />
                ),
              )}
            </tbody>
          </table>
        </div>
        {rows.length >= QUEUE_LIMIT && (
          <div className="qnote">
            Zobrazeno prvních {QUEUE_LIMIT} vozů. Fronta je delší — takový počet
            nerozdělených převozů stojí za pohled.
          </div>
        )}
      </div>

      <div className="card legenda">
        <h3>Legenda</h3>
        <div className="items">
          <span>
            <span className="sw" style={{ background: 'var(--row-paid)' }} /> řidič přiřazen
          </span>
          <span>
            <span className="sw" style={{ background: 'var(--critical-bg)' }} /> odjezd do 2 hodin
          </span>
          <span>
            <span
              className="sw"
              style={{ background: 'color-mix(in srgb,var(--link) 12%,var(--surface))' }}
            />{' '}
            stání v Europortu
          </span>
          <span>
            <span className="sw" style={{ background: 'var(--chip)' }} /> stání mimo Europort —
            Tuchoměřice, POP, Kněževes, Šestka
          </span>
          <span>
            <span className="sw" style={{ background: 'var(--critical-bg)' }} /> stání nebo SPZ
            chybí
          </span>
        </div>
      </div>

      <Footer />
    </div>
  );
}

/** 'Svatopluk Ondráček' -> 'Svatopluk O.'; a one-word name passes through. */
function shortName(full: string): string {
  const parts = full.trim().split(/\s+/);
  if (parts.length < 2) return full;
  return `${parts[0]} ${parts[parts.length - 1]![0]}.`;
}
